import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, ArrowLeft } from 'lucide-react';
import { publicAPI } from '../../services/api';
import LandingNavbar from '../../components/LandingNavbar';

/* ─────────────────────────────────────────────────────────────────── */
/*  STATIC FEATURE TABLE — all content is hardcoded, only prices      */
/*  are fetched from backend                                          */
/* ─────────────────────────────────────────────────────────────────── */

const PLAN_SLUGS = ['basic', 'professional', 'enterprise'];
const PLAN_NAMES = ['الباقة الأساسية', 'الباقة المتقدمة', 'الباقة الاحترافية'];
const PLAN_COLORS = ['#3B82F6', '#0F6E56', '#7C3AED'];
const PLAN_OLD_PRICES = [3000, 5000, 8500]; // strikethrough "was" prices

const FEATURE_GROUPS = [
  {
    title: 'الأنظمة الأساسية',
    features: [
      { label: 'نظام إدارة عمليات المختبرات (LIS)', basic: true, professional: true, enterprise: true },
      { label: 'نظام التقارير المالية', basic: true, professional: true, enterprise: true },
      { label: 'بوابة المرضى الإلكترونية', basic: true, professional: true, enterprise: true },
      { label: 'نظام الفوترة الإلكترونية ZATCA', basic: true, professional: true, enterprise: true },
      { label: '+201 قالب تحليل جاهز بالمعايير المرجعية', basic: true, professional: true, enterprise: true },
    ]
  },
  {
    title: 'خصائص الباقات',
    features: [
      { label: 'عدد العملاء (المرضى) شهرياً', basic: 'غير محدود', professional: 'غير محدود', enterprise: 'غير محدود' },
      { label: 'عدد المستخدمين', basic: '5', professional: '15', enterprise: 'غير محدود' },
      { label: 'تحليلات الذكاء الاصطناعي شهرياً', basic: '5,000', professional: '10,000', enterprise: '20,000' },
      { label: 'ربط أجهزة التحليل بالنظام', basic: '3 أجهزة', professional: '5 أجهزة', enterprise: '7 أجهزة' },
      { label: 'مراقبة الجودة (QC) — قواعد Westgard', basic: false, professional: true, enterprise: true },
      { label: 'بوابة المختبرات المتعاقدة', basic: false, professional: true, enterprise: true },
      { label: 'براندنج مخصص (شعار + ألوان)', basic: false, professional: true, enterprise: true },
      { label: 'محادثات داخلية للفريق', basic: true, professional: true, enterprise: true },
      { label: 'سير عمل اعتماد النتائج', basic: true, professional: true, enterprise: true },
      { label: 'كوبونات خصم', basic: true, professional: true, enterprise: true },
      { label: 'إشعارات SMS (مزوّد خاص بك)', basic: true, professional: true, enterprise: true },
      { label: 'تسويق WhatsApp', basic: false, professional: true, enterprise: true },
      { label: 'زيارات منزلية', basic: false, professional: true, enterprise: true },
      { label: 'علم الأحياء الدقيقة والمزارع', basic: false, professional: true, enterprise: true },
      { label: 'صلاحيات وأدوار مخصصة', basic: true, professional: true, enterprise: true },
      { label: 'ويدجت حجز للموقع', basic: false, professional: false, enterprise: true },
      { label: 'تصدير البيانات', basic: true, professional: true, enterprise: true },
    ]
  },
  {
    title: 'الرسوم الإضافية',
    features: [
      { label: 'رسوم التأسيس والإعداد', basic: 'مجاناً', professional: 'مجاناً', enterprise: 'مجاناً' },
      { label: 'تكلفة ربط جهاز تحليل إضافي (لمرة واحدة)', basic: '2,500 ر.س', professional: '2,500 ر.س', enterprise: '2,500 ر.س' },
      // SMS fee row deferred — each lab will plug in its own SMS provider
      // (مزوّد الرسائل) API keys from the settings page, so pricing is
      // controlled by the tenant, not us. Re-add when a default managed-SMS
      // tier launches.
      // Additional-branch cost row removed — branch pricing is negotiated
      // per-case rather than being a published line item.
    ]
  }
];

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.getPlans()
      .then(res => {
        const data = res.data?.data || res.data;
        setPlans(Array.isArray(data) ? data : []);
      })
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  // Map slugs to fetched plan data (for prices only)
  const getPlanPrice = (slug) => {
    const plan = plans.find(p => p.slug === slug);
    if (!plan) return { monthly: '—', yearly: '—', trial: 0 };
    return {
      monthly: plan.monthlyPrice,
      yearly: plan.yearlyPrice,
      trial: plan.trialDays || 0,
    };
  };

  const renderCellValue = (val) => {
    if (val === true) return <Check className="w-5 h-5 text-[#0F6E56] mx-auto" />;
    if (val === false) return <X className="w-4 h-4 text-gray-300 mx-auto" />;
    return <span className="text-[13px] font-medium text-[#1A1A18]">{val}</span>;
  };

  return (
    <div dir="rtl" className="bg-[#FDFCF9] text-[#1A1A18] min-h-screen" style={{ fontFamily: "'Readex Pro', sans-serif" }}>
      <LandingNavbar />

      {/* Header */}
      <section className="pt-32 sm:pt-36 pb-12 sm:pb-16 text-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-[12px] font-semibold tracking-widest uppercase text-[#8A8780]">الأسعار</span>
          <h1 className="text-[1.8rem] sm:text-[2.5rem] lg:text-[3rem] font-bold mt-3 mb-4">
            اختر الباقة المناسبة لأعمالك
          </h1>
          <p className="text-[14px] sm:text-[15px] text-[#4A4940] max-w-[480px] mx-auto leading-relaxed">
            باقات شهرية مرنة لجميع أحجام المختبرات — ابدأ مجاناً بدون بطاقة ائتمان.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 bg-white rounded-full p-1 border border-[#E8E5DD] mt-8">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 ${billingCycle === 'monthly' ? 'bg-[#0F6E56] text-white shadow-sm' : 'text-[#4A4940] hover:text-[#1A1A18]'}`}
            >
              شهري
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 ${billingCycle === 'yearly' ? 'bg-[#0F6E56] text-white shadow-sm' : 'text-[#4A4940] hover:text-[#1A1A18]'}`}
            >
              سنوي
            </button>
          </div>
        </motion.div>
      </section>

      {/* Pricing Cards — 3 columns */}
      <section className="max-w-[1100px] mx-auto px-4 sm:px-8 mb-16">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#E8E5DD] border-t-[#0F6E56] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5">
            {PLAN_SLUGS.map((slug, i) => {
              const p = getPlanPrice(slug);
              const price = billingCycle === 'yearly' ? Math.round(p.yearly / 12) : p.monthly;
              const oldPrice = PLAN_OLD_PRICES[i];
              const isPopular = i === 1;
              return (
                <motion.div
                  key={slug}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`relative bg-white rounded-2xl border p-6 sm:p-7 flex flex-col ${isPopular ? 'border-[#0F6E56] shadow-lg shadow-[#0F6E56]/10' : 'border-[#E8E5DD]'}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0F6E56] text-white text-[11px] font-bold px-4 py-1 rounded-full">
                      الأكثر طلبًا
                    </div>
                  )}
                  <h3 className="text-lg font-bold mb-1" style={{ color: PLAN_COLORS[i] }}>{PLAN_NAMES[i]}</h3>
                  <div className="mt-3 mb-5">
                    <div className="text-[14px] text-red-400 line-through mb-1">{oldPrice.toLocaleString('ar-SA-u-ca-gregory')} ر.س/شهر</div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold">{typeof price === 'number' ? price.toLocaleString('ar-SA-u-ca-gregory') : price}</span>
                      <span className="text-[13px] text-[#8A8780]">ر.س / شهر</span>
                    </div>
                    {p.trial > 0 && (
                      <div className="text-[12px] text-[#1D9E75] mt-1 font-medium">
                        {p.trial} يوم تجربة مجانية — بدون بطاقة
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/register?plan=${slug}`}
                    className={`block w-full py-3 rounded-xl text-[14px] font-semibold text-center transition-colors ${isPopular ? 'bg-[#0F6E56] text-white hover:bg-[#0d5f4a]' : 'border border-[#E8E5DD] text-[#1A1A18] hover:border-[#ccc9c0] hover:bg-[#F7F6F1]'}`}
                  >
                    {p.trial > 0 ? 'جرّب مجانًا' : 'اشترك الآن'}
                    {' '}←
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* Feature Comparison Table */}
      <section className="max-w-[1100px] mx-auto px-4 sm:px-8 pb-20">
        <div className="bg-white rounded-2xl border border-[#E8E5DD] overflow-hidden">
          {/* Table header — plan names */}
          <div className="grid grid-cols-4 border-b border-[#E8E5DD] bg-[#F7F6F1]">
            <div className="p-4 sm:p-5 text-[14px] font-bold text-[#4A4940]">المقارنة التفصيلية</div>
            {PLAN_NAMES.map((name, i) => (
              <div key={i} className="p-3 sm:p-5 text-center">
                <span className="text-[13px] sm:text-[14px] font-bold" style={{ color: PLAN_COLORS[i] }}>{name}</span>
              </div>
            ))}
          </div>

          {/* Feature groups */}
          {FEATURE_GROUPS.map((group, gi) => (
            <div key={gi}>
              {/* Group header */}
              <div className="px-4 sm:px-5 py-3 bg-[#F7F6F1]/60 border-b border-[#E8E5DD]">
                <span className="text-[13px] font-bold text-[#0F6E56]">{group.title}</span>
              </div>
              {/* Feature rows */}
              {group.features.map((feat, fi) => (
                <div
                  key={fi}
                  className={`grid grid-cols-4 ${fi < group.features.length - 1 ? 'border-b border-[#F0EFEA]' : gi < FEATURE_GROUPS.length - 1 ? 'border-b border-[#E8E5DD]' : ''}`}
                >
                  <div className="p-3 sm:p-4 text-[12px] sm:text-[13px] text-[#4A4940] leading-relaxed flex items-center">
                    {feat.label}
                  </div>
                  <div className="p-3 sm:p-4 text-center flex items-center justify-center">
                    {renderCellValue(feat.basic)}
                  </div>
                  <div className="p-3 sm:p-4 text-center flex items-center justify-center">
                    {renderCellValue(feat.professional)}
                  </div>
                  <div className="p-3 sm:p-4 text-center flex items-center justify-center">
                    {renderCellValue(feat.enterprise)}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* CTA row */}
          <div className="grid grid-cols-4 bg-[#F7F6F1] border-t border-[#E8E5DD]">
            <div className="p-4" />
            {PLAN_SLUGS.map((slug, i) => (
              <div key={i} className="p-3 sm:p-4 text-center">
                <Link
                  to={`/register?plan=${slug}`}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-[12px] sm:text-[13px] font-semibold transition-colors"
                  style={{
                    backgroundColor: i === 1 ? '#0F6E56' : 'transparent',
                    color: i === 1 ? 'white' : '#0F6E56',
                    border: i === 1 ? 'none' : '1px solid #0F6E56'
                  }}
                >
                  ابدأ الآن <ArrowLeft className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="text-center pb-12 text-[13px] text-[#8A8780]">
        جميع الأسعار بالريال السعودي ولا تشمل ضريبة القيمة المضافة (15%)
      </div>
    </div>
  );
}
